import {
  GOOD_SCORE,
  GOOD_WINDOW_MS,
  HP_EMPTY_TAP,
  HP_EMPTY_TAP_GRACE_HITS,
  HP_EMPTY_TAP_NEARBY_BEATS,
  HP_GOOD,
  HP_MISS_DAMAGE_BY_STREAK_INDEX,
  HP_PERFECT,
  HP_REGEN_INTERVAL_BEATS,
  HP_REGEN_PER_TICK,
  HP_REGEN_QUIET_BEATS,
  LIFELINE_HP_THRESHOLD,
  MISS_SCORE,
  PERFECT_SCORE,
  PERFECT_WINDOW_MS,
} from '../../gameTypes.js';
import type { ManiaChart, ManiaLane } from '../../maniaNote.js';
import {
  comboMultiplier,
  computeAccuracy,
  judge,
  type JudgeResult,
} from '../../scoring.js';
import {
  buildLaneEventLookup,
  type ActiveHold,
  type ManiaDeathReason,
  type ManiaEngineConfig,
  type ManiaEngineState,
  type ManiaLaneEvent,
  type ManiaResolution,
} from './state.js';

/**
 * Origin device of a {@link ManiaEngineInput}. The engine uses this to
 * pick between two early-tap "good" windows: keyboard input is held
 * to a tight window so PC players can still genuinely miss by jumping
 * the gun, while pointer (touch / mouse) input is given the full
 * visible approach because the player already couples lane +
 * vertical targeting into one physical gesture.
 *
 * The perfect (geometric overlap) and late-edge auto-miss are
 * device-agnostic — only the EARLY side of judging branches on this.
 */
export type ManiaInputDevice = 'keyboard' | 'pointer';

/**
 * One player input. The mania input model is a discrete press / release
 * per lane. Both `down` (tap a tile / start a hold) and `up` (end a
 * hold) are scoring inputs — `up` events on lanes that aren't currently
 * holding a tile are silently ignored.
 */
export interface ManiaEngineInput {
  beat: number;
  lane: ManiaLane;
  type: 'down' | 'up';
  source: ManiaInputDevice;
}

/**
 * Per-mode tuning the engine needs at config-build time. Lives outside
 * the chart shape because these fractions are coupled to the renderer's
 * tile geometry (Piano vs Guitar tile heights / hit-line positions),
 * not to the chart payload.
 *
 * - `perfectOverlapFrac`: fraction of `approachMs` the tile spends
 *   visually overlapping the hit bar. The engine grants `perfect` for
 *   `diffMs in [0, approachMs × perfectOverlapFrac]`.
 * - `keyboardGoodEarlyFrac`: early-tap good window for keyboard input,
 *   as a fraction of `approachMs`. Tighter than pointer because PC
 *   players have only timing to worry about.
 * - `pointerGoodEarlyFrac`: early-tap good window for pointer (touch /
 *   mouse) input, as a fraction of `approachMs`. Wider — touch already
 *   gates itself via the player having to physically touch the tile.
 * - `keyboardGoodLateFrac` / `pointerGoodLateFrac` (optional, default
 *   0): late-tap good slack after the geometric perfect window ends.
 *   A strum in `(perfectOverlapMs, perfectOverlapMs + lateGoodMs]`
 *   scores `good`; auto-miss waits until the longer of the two late
 *   windows has elapsed.
 * - `missDamageMultiplier` (optional, default 1.0): scales the
 *   per-miss HP damage from {@link HP_MISS_DAMAGE_BY_STREAK_INDEX}.
 *   Modes with tighter timing windows + denser charts (Guitar) miss
 *   more often than Piano on the same skill level, so they get a
 *   damage reduction to keep the run-length feel comparable while
 *   keeping the harder timing as the real skill ceiling.
 * - `instadeathOnMiss` (optional, default false): when true, a single
 *   miss ends the run immediately (no HP buffer, no lifeline). Piano's
 *   real play uses this so a flawless clear is the only win; Guitar and
 *   Piano's tutorial leave it off and keep the HP-drain model.
 */
export interface ManiaEngineTuning {
  perfectOverlapFrac: number;
  keyboardGoodEarlyFrac: number;
  pointerGoodEarlyFrac: number;
  keyboardGoodLateFrac?: number;
  pointerGoodLateFrac?: number;
  missDamageMultiplier?: number;
  instadeathOnMiss?: boolean;
}

/**
 * Late-release auto-resolve grace, expressed as a multiple of the
 * good window. The player can hang on to a hold past `holdEndBeat`
 * for `LATE_RELEASE_GRACE_GOOD_WINDOWS × goodWindow` before the
 * engine forces the release as a `good` (lenient — they kept their
 * finger down so the body language is "completed", just sloppy on
 * the timing).
 *
 * Tuned at 1.5× because a 1.0× cutoff felt unfair on dense charts —
 * the player needs about a frame of post-release slack to lift their
 * finger before the next tap, and 0.5× extra window gives that with
 * room to spare. Mirrors the philosophy of Circles' slider-tail
 * tolerance (lenient on the late edge, strict on the early edge).
 */
const LATE_RELEASE_GRACE_GOOD_WINDOWS = 1.5;

/**
 * Early-release auto-resolve grace, expressed as the *additional*
 * window beyond `holdEndBeat - goodWindow` inside which an early
 * release demotes to `good` instead of failing the whole hold as a
 * broken-release `miss`. Total early cutoff =
 * `goodWindowMs × (1 + EARLY_RELEASE_GRACE_GOOD_WINDOWS)` —
 * mirroring {@link LATE_RELEASE_GRACE_GOOD_WINDOWS}'s `1 +` shape.
 *
 * The motivation is touchscreen play: on phones the player has to
 * lift their thumb a tile or two before the next tap to physically
 * reposition it, which on dense holds lands a few hundred ms before
 * the official `holdEndBeat`. Without the grace those "I was nearly
 * there" releases punish thumb mechanics rather than timing skill.
 *
 * Set deliberately larger than {@link LATE_RELEASE_GRACE_GOOD_WINDOWS}
 * (3.5 vs 1.5) — the asymmetry is intentional. Late grace is
 * "you held a beat or two too long" which is a musical mistake; early
 * grace is "your thumb already had to be on the next tile" which is
 * an ergonomic necessity on touch. With the default 120 ms good
 * window the total early cutoff lands at 540 ms (≈1 beat at 120
 * BPM): the 120 ms on-time good window plus 3.5 × 120 ms = 420 ms
 * of grace beyond it — wide enough for realistic thumb-relocation
 * latency without letting players cheese long holds.
 *
 * A release earlier than this cutoff is still a broken hold (combo
 * break + miss): the player clearly let go too soon to count as
 * "completed", just like a release way past holdEndBeat.
 */
const EARLY_RELEASE_GRACE_GOOD_WINDOWS = 3.5;

export interface ManiaHpChangedPayload {
  delta: number;
  hp: number;
  reason: 'perfect' | 'good' | 'miss' | 'empty_tap' | 'regen' | 'lifeline' | 'drained';
}

export interface ManiaEngineEvent {
  type:
    | 'tap'
    | 'note_judged'
    | 'completed'
    | 'hp_changed';
  beat: number;
  payload?: unknown;
}

export interface ManiaStepInput {
  config: ManiaEngineConfig;
  state: ManiaEngineState;
  fromBeat: number;
  toBeat: number;
  inputs: ManiaEngineInput[];
}

export interface ManiaStepOutput {
  state: ManiaEngineState;
  events: ManiaEngineEvent[];
}

export function bpmToBeatsPerMs(bpm: number): number {
  return bpm / 60_000;
}

/**
 * Beat-window floor used when the chart's BPM × ms-window product
 * collapses to ~0 (extremely slow songs or near-zero ms windows). A
 * one-thousandth-of-a-beat floor stops `findClosestOpenEvent` from
 * rejecting every candidate via floating-point underflow on those
 * edge cases — same trick the Circles + Pulse engines use.
 */
const HIT_HALF_BEAT_WINDOW_FALLBACK = 0.001;

export function buildManiaEngineConfig(
  chart: ManiaChart,
  tuning: ManiaEngineTuning,
): ManiaEngineConfig {
  const beatsPerMs = bpmToBeatsPerMs(chart.bpm);
  const perfectMs = chart.perfectWindowMs ?? PERFECT_WINDOW_MS;
  const goodMs = chart.goodWindowMs ?? GOOD_WINDOW_MS;
  const msPerBeat = beatsPerMs > 0 ? 1 / beatsPerMs : 0;
  const approachMs = chart.approachMs;
  const approachBeats =
    beatsPerMs > 0 ? approachMs * beatsPerMs : approachMs / Math.max(msPerBeat, 1);
  const perfectOverlapMs = approachMs * tuning.perfectOverlapFrac;
  const keyboardGoodEarlyMs = approachMs * tuning.keyboardGoodEarlyFrac;
  const pointerGoodEarlyMs = approachMs * tuning.pointerGoodEarlyFrac;
  const keyboardGoodLateMs = approachMs * (tuning.keyboardGoodLateFrac ?? 0);
  const pointerGoodLateMs = approachMs * (tuning.pointerGoodLateFrac ?? 0);
  const keyboardGoodLateBeats = Math.max(
    keyboardGoodLateMs * beatsPerMs,
    0,
  );
  const pointerGoodLateBeats = Math.max(
    pointerGoodLateMs * beatsPerMs,
    0,
  );
  const autoMissLateBeats = Math.max(keyboardGoodLateBeats, pointerGoodLateBeats);
  return {
    chart,
    sortedLaneEvents: buildLaneEventLookup(chart.notes),
    perfectWindowMs: perfectMs,
    goodWindowMs: goodMs,
    goodWindowBeats: Math.max(goodMs * beatsPerMs, HIT_HALF_BEAT_WINDOW_FALLBACK),
    perfectOverlapMs,
    perfectOverlapBeats: Math.max(
      perfectOverlapMs * beatsPerMs,
      HIT_HALF_BEAT_WINDOW_FALLBACK,
    ),
    keyboardGoodEarlyMs,
    keyboardGoodEarlyBeats: Math.max(
      keyboardGoodEarlyMs * beatsPerMs,
      HIT_HALF_BEAT_WINDOW_FALLBACK,
    ),
    pointerGoodEarlyMs,
    pointerGoodEarlyBeats: Math.max(
      pointerGoodEarlyMs * beatsPerMs,
      HIT_HALF_BEAT_WINDOW_FALLBACK,
    ),
    keyboardGoodLateMs,
    keyboardGoodLateBeats,
    pointerGoodLateMs,
    pointerGoodLateBeats,
    autoMissLateBeats,
    approachBeats,
    approachMs,
    missDamageMultiplier: tuning.missDamageMultiplier ?? 1,
    instadeathOnMiss: tuning.instadeathOnMiss ?? false,
  };
}

/**
 * Geometric head-tap judgement. The perfect tier tracks the tile's
 * actual visual overlap with the hit bar rather than a tight ms
 * window around the target beat — this matches how falling-tile
 * rhythm games "feel" to play and removes the confusing edge where
 * a tile is still clearly over the bar but the verdict reads miss.
 *
 *   diffMs in [0, perfectOverlapMs]            → perfect
 *   diffMs in [-earlyGoodMs, 0)                → good
 *   diffMs in (perfectOverlapMs, perfect+late] → good (late slack)
 *   diffMs > perfectOverlapMs + lateGoodMs     → miss (tile past bar)
 *   diffMs < -earlyGoodMs                      → miss (tile too high)
 *
 * `earlyGoodMs` branches on input source:
 *   - keyboard: tight (`config.keyboardGoodEarlyMs`) so the player
 *     can still miss by mashing on a tile that's nowhere near the
 *     bar yet — keyboard play has no vertical-targeting cost.
 *   - pointer: full visible approach (`config.pointerGoodEarlyMs`)
 *     so any tap on a visible tile resolves; touch already gates
 *     itself via the player having to physically touch the tile.
 *
 * Hold releases bypass this function and use the symmetric `judge()`
 * helper against `holdEndBeat` instead — the release event has no
 * visual "overlap" anchor; it's a moment in time the player has to
 * nail.
 */
function judgeManiaTap(
  diffMs: number,
  source: ManiaInputDevice,
  config: ManiaEngineConfig,
): JudgeResult {
  if (diffMs >= 0 && diffMs <= config.perfectOverlapMs) {
    return { verdict: 'perfect', diffMs, baseScore: PERFECT_SCORE };
  }
  if (diffMs > config.perfectOverlapMs) {
    const lateGoodMs =
      source === 'pointer' ? config.pointerGoodLateMs : config.keyboardGoodLateMs;
    if (lateGoodMs > 0 && diffMs <= config.perfectOverlapMs + lateGoodMs) {
      return { verdict: 'good', diffMs, baseScore: GOOD_SCORE };
    }
    return { verdict: 'miss', diffMs, baseScore: MISS_SCORE };
  }
  const earlyMs = -diffMs;
  const earlyGoodMs =
    source === 'pointer' ? config.pointerGoodEarlyMs : config.keyboardGoodEarlyMs;
  if (earlyMs <= earlyGoodMs) {
    return { verdict: 'good', diffMs, baseScore: GOOD_SCORE };
  }
  return { verdict: 'miss', diffMs, baseScore: MISS_SCORE };
}

interface ApplyHpDeltaOpts {
  /**
   * Skip the one-time lifeline floor so a fatal delta actually kills
   * even from full HP. Used by instadeath-on-miss, where the whole
   * point is that a single slip ends the run.
   */
  bypassLifeline?: boolean;
  /**
   * Death reason to record when this delta drains HP to zero. Defaults
   * to `'drained'`; instadeath passes `'missed'` so the results copy
   * reads "you missed a note" rather than "your HP hit zero".
   */
  deathReason?: ManiaDeathReason;
}

function applyHpDelta(
  state: ManiaEngineState,
  beat: number,
  delta: number,
  reason: ManiaHpChangedPayload['reason'],
  events: ManiaEngineEvent[],
  opts: ApplyHpDeltaOpts = {},
): void {
  if (delta === 0) {
    return;
  }
  if (delta < 0) {
    state.combo = 0;
    state.regenProgressBeats = 0;
  }
  const wasHealthy = state.hp > LIFELINE_HP_THRESHOLD;
  const oldHp = state.hp;
  const rawNewHp = state.hp + delta;
  let lifelineCaught = false;
  if (delta < 0 && !opts.bypassLifeline && wasHealthy && rawNewHp <= 0) {
    state.hp = 1;
    lifelineCaught = true;
  } else {
    state.hp = Math.max(0, Math.min(state.maxHp, rawNewHp));
  }
  const drained = state.hp <= 0;
  const effectiveReason: ManiaHpChangedPayload['reason'] = drained
    ? 'drained'
    : lifelineCaught
      ? 'lifeline'
      : reason;
  const payload: ManiaHpChangedPayload = {
    delta: state.hp - oldHp,
    hp: state.hp,
    reason: effectiveReason,
  };
  events.push({ type: 'hp_changed', beat, payload });
  if (drained) {
    state.alive = false;
    state.deathReason = opts.deathReason ?? 'drained';
    state.deathBeat = beat;
  }
}

function missDamageForStreak(streak: number): number {
  const table = HP_MISS_DAMAGE_BY_STREAK_INDEX;
  const idx = Math.min(streak, table.length - 1);
  return table[idx];
}

function applyMissDamage(
  state: ManiaEngineState,
  config: ManiaEngineConfig,
  beat: number,
  events: ManiaEngineEvent[],
): void {
  const baseDamage = missDamageForStreak(state.consecutiveMissCount);
  // Round to integer so HP stays whole-numbered regardless of the
  // multiplier. Without this a 0.6× scale of the -6/-5/-4 table
  // produces -3.6/-3.0/-2.4 and HP accumulates fractional residue —
  // currently harmless but creates float-order sensitivity for any
  // future reordering of HP math or replay re-batch and breaks any
  // future `Number.isInteger`-style assertion on `state.hp`.
  const damage = Math.round(baseDamage * config.missDamageMultiplier);
  state.consecutiveMissCount += 1;
  state.lastMissBeat = beat;
  if (config.instadeathOnMiss) {
    // Drain straight to zero (bypassing the lifeline) so a single miss
    // ends the run. Routing through `applyHpDelta` keeps the death
    // path identical to a slow drain — same `'drained'` hp_changed
    // event drives the existing GAME-OVER FX — while `deathReason`
    // records `'missed'` for the results screen.
    applyHpDelta(state, beat, -state.hp, 'miss', events, {
      bypassLifeline: true,
      deathReason: 'missed',
    });
    return;
  }
  applyHpDelta(state, beat, damage, 'miss', events);
}

/**
 * Whether any unresolved lane event in this lane lies within
 * HP_EMPTY_TAP_NEARBY_BEATS of the given beat. Mirrors the
 * Pulse/Circles rule: empty taps in dead air are free, but tapping
 * when there's a real target close enough in time costs HP after
 * the onboarding grace.
 *
 * The nearby check is **lane-scoped** (not chart-wide) because the
 * player has dedicated input per lane — tapping in lane 0 when
 * there's an unresolved tile in lane 3 doesn't read as "missing the
 * lane 3 target", it reads as "ghost-tapping in dead air".
 */
function hasUnresolvedEventNearby(
  laneEvents: ReadonlyArray<ManiaLaneEvent>,
  resolvedIds: ReadonlySet<string>,
  lane: ManiaLane,
  beat: number,
): boolean {
  for (const ev of laneEvents) {
    if (ev.lane !== lane) {
      continue;
    }
    if (resolvedIds.has(ev.id)) {
      continue;
    }
    if (Math.abs(ev.beat - beat) <= HP_EMPTY_TAP_NEARBY_BEATS) {
      return true;
    }
  }
  return false;
}

/**
 * Find the unresolved lane event the tap should resolve. The match
 * window is asymmetric and source-aware to mirror
 * {@link judgeManiaTap}'s windows:
 *
 *   late edge:   `perfectOverlapBeats + autoMissLateBeats` (same for
 *                both sources — matches {@link expireMissedEvents})
 *   early edge:  `keyboardGoodEarlyBeats` for keyboard input,
 *                `pointerGoodEarlyBeats` for pointer input
 *
 * Closeness within the window is measured by absolute beat-distance
 * so a tap roughly between two candidates picks the nearer one.
 * Beyond the window the tap is treated as an empty tap (the engine
 * doesn't manufacture miss verdicts on user input — misses come
 * from {@link expireMissedEvents} when the tile fully passes the
 * bar without a tap).
 */
function findClosestOpenEvent(
  laneEvents: ReadonlyArray<ManiaLaneEvent>,
  resolvedIds: ReadonlySet<string>,
  inp: ManiaEngineInput,
  config: ManiaEngineConfig,
): ManiaLaneEvent | null {
  const earlyBeats =
    inp.source === 'pointer'
      ? config.pointerGoodEarlyBeats
      : config.keyboardGoodEarlyBeats;
  const lateBeats = config.perfectOverlapBeats + config.autoMissLateBeats;
  let best: { ev: ManiaLaneEvent; diff: number } | null = null;
  for (const ev of laneEvents) {
    if (ev.lane !== inp.lane) {
      continue;
    }
    if (resolvedIds.has(ev.id)) {
      continue;
    }
    const diff = inp.beat - ev.beat;
    if (diff < -earlyBeats || diff > lateBeats) {
      continue;
    }
    const absDiff = Math.abs(diff);
    if (!best || absDiff < Math.abs(best.diff)) {
      best = { ev, diff };
    }
  }
  return best?.ev ?? null;
}

function resolveTap(
  state: ManiaEngineState,
  config: ManiaEngineConfig,
  ev: ManiaLaneEvent,
  input: ManiaEngineInput,
  events: ManiaEngineEvent[],
): ManiaResolution {
  const beatsPerMs = bpmToBeatsPerMs(config.chart.bpm);
  const diffMs = (input.beat - ev.beat) / beatsPerMs;
  const j = judgeManiaTap(diffMs, input.source, config);
  const previousCombo = state.combo;

  // Compute the score increment now, before mutating combo, so the
  // resolution carries the same value that lands on `state.score` —
  // single source of truth for downstream consumers (score-pops,
  // telemetry, future replay summaries).
  let scoreDelta = 0;
  state.resolvedIds.add(ev.id);

  if (j.verdict === 'perfect') {
    state.perfects += 1;
    state.combo += 1;
    scoreDelta = Math.round(j.baseScore * comboMultiplier(state.combo));
    state.score += scoreDelta;
  } else if (j.verdict === 'good') {
    state.goods += 1;
    state.combo += 1;
    scoreDelta = Math.round(j.baseScore * comboMultiplier(state.combo));
    state.score += scoreDelta;
  } else {
    state.misses += 1;
    state.combo = 0;
  }
  if (state.combo > state.maxCombo) {
    state.maxCombo = state.combo;
  }

  const resolution: ManiaResolution = {
    laneEventId: ev.id,
    sourceId: ev.sourceId,
    lane: ev.lane,
    kind: 'head',
    verdict: j.verdict,
    diffMs,
    beat: input.beat,
    previousCombo,
    scoreDelta,
  };
  state.resolutions.push(resolution);
  events.push({ type: 'note_judged', beat: input.beat, payload: resolution });

  if (j.verdict === 'perfect') {
    state.consecutiveMissCount = 0;
    applyHpDelta(state, input.beat, HP_PERFECT, 'perfect', events);
  } else if (j.verdict === 'good') {
    state.consecutiveMissCount = 0;
    applyHpDelta(state, input.beat, HP_GOOD, 'good', events);
  } else {
    applyMissDamage(state, config, input.beat, events);
  }

  // Engage the hold on a successful head hit: subsequent `down`s on
  // this lane will be ignored (no empty-tap penalty for "finger still
  // pressing the same key"), and the next `up` will be judged against
  // `ev.holdEndBeat`. A missed head terminates the hold up front —
  // there's nothing to release if the player never engaged it.
  if (ev.holdEndBeat !== undefined && j.verdict !== 'miss') {
    state.heldLanes.set(ev.lane, {
      laneEventId: ev.id,
      sourceId: ev.sourceId,
      lane: ev.lane,
      holdEndBeat: ev.holdEndBeat,
    });
  }

  return resolution;
}

/**
 * Judge a release input against an in-flight hold. Releases use the
 * symmetric ms-window {@link judge} against `holdEndBeat` (not the
 * geometric model that head taps use — a release is a moment in
 * time, not a visual overlap event). Scoring shape, HP deltas, and
 * combo carry mirror {@link resolveTap}'s outputs; only the verdict
 * derivation differs:
 *   - Early release (way before `holdEndBeat`, outside good window):
 *     broken hold → `miss`.
 *   - On-time release (inside perfect/good window): tap-shaped
 *     verdict from `judge()`.
 *   - Late release inside the auto-grace cutoff
 *     (`goodWindowMs × (1 + LATE_RELEASE_GRACE_GOOD_WINDOWS)`):
 *     demoted to `good` (lenient — finger stayed down).
 *   - Past the grace cutoff: full `miss`.
 *
 * In practice `expireStaleHolds` fires before the player can release
 * way past the grace, but the explicit cutoff here keeps the two
 * paths from drifting if one is reordered or its constant changes.
 */
function resolveHoldRelease(
  state: ManiaEngineState,
  config: ManiaEngineConfig,
  hold: ActiveHold,
  releaseBeat: number,
  events: ManiaEngineEvent[],
): ManiaResolution {
  const beatsPerMs = bpmToBeatsPerMs(config.chart.bpm);
  const diffMs = (releaseBeat - hold.holdEndBeat) / beatsPerMs;
  // Four-zone verdict for releases (symmetric on early / late):
  //   - On-time (within perfect/good window): judge like a tap.
  //   - Late but within the late-grace window: demote to `good`
  //     (lenient — the player kept their finger down, the engine
  //     was about to auto-resolve anyway).
  //   - Early but within the early-grace window: demote to `good`
  //     (lenient — touchscreen play needs a small head-start to
  //     reposition the thumb onto the next tile).
  //   - Past either grace cutoff: full `miss` (broken hold). The
  //     late cutoff explicitly references the same constant that
  //     drives expireStaleHolds so the two stay in lockstep.
  const lateCutoffMs = config.goodWindowMs * (1 + LATE_RELEASE_GRACE_GOOD_WINDOWS);
  const earlyCutoffMs = config.goodWindowMs * (1 + EARLY_RELEASE_GRACE_GOOD_WINDOWS);
  const j = judge(diffMs, 0, {
    perfectWindowMs: config.perfectWindowMs,
    goodWindowMs: config.goodWindowMs,
  });
  let verdict: typeof j.verdict;
  let baseScore: number;
  if (j.verdict !== 'miss') {
    verdict = j.verdict;
    baseScore = j.baseScore;
  } else if (diffMs > 0 && diffMs <= lateCutoffMs) {
    verdict = 'good';
    baseScore = GOOD_SCORE;
  } else if (diffMs < 0 && -diffMs <= earlyCutoffMs) {
    verdict = 'good';
    baseScore = GOOD_SCORE;
  } else {
    verdict = 'miss';
    baseScore = 0;
  }

  const previousCombo = state.combo;
  let scoreDelta = 0;
  state.resolvedReleaseIds.add(hold.laneEventId);
  state.heldLanes.delete(hold.lane);

  if (verdict === 'perfect') {
    state.perfects += 1;
    state.combo += 1;
    scoreDelta = Math.round(baseScore * comboMultiplier(state.combo));
    state.score += scoreDelta;
  } else if (verdict === 'good') {
    state.goods += 1;
    state.combo += 1;
    scoreDelta = Math.round(baseScore * comboMultiplier(state.combo));
    state.score += scoreDelta;
  } else {
    state.misses += 1;
    state.combo = 0;
  }
  if (state.combo > state.maxCombo) {
    state.maxCombo = state.combo;
  }

  const resolution: ManiaResolution = {
    laneEventId: hold.laneEventId,
    sourceId: hold.sourceId,
    lane: hold.lane,
    kind: 'release',
    verdict,
    diffMs,
    beat: releaseBeat,
    previousCombo,
    scoreDelta,
  };
  state.resolutions.push(resolution);
  events.push({ type: 'note_judged', beat: releaseBeat, payload: resolution });

  if (verdict === 'perfect') {
    state.consecutiveMissCount = 0;
    applyHpDelta(state, releaseBeat, HP_PERFECT, 'perfect', events);
  } else if (verdict === 'good') {
    state.consecutiveMissCount = 0;
    applyHpDelta(state, releaseBeat, HP_GOOD, 'good', events);
  } else {
    applyMissDamage(state, config, releaseBeat, events);
  }

  return resolution;
}

/**
 * Beat at which an unresolved lane-event head auto-misses. The cutoff
 * mirrors {@link judgeManiaTap}'s late-perfect boundary: a tile is
 * declared missed once its trailing edge has crossed the hit bar (i.e.
 * it's no longer visually overlapping the bar), so the engine and the
 * player's eye agree on "you've run out of time." The offset is a
 * chart-wide constant, so expiry order equals `sortedLaneEvents` order.
 */
function noteExpiryBeat(config: ManiaEngineConfig, ev: ManiaLaneEvent): number {
  return ev.beat + config.perfectOverlapBeats + config.autoMissLateBeats;
}

/**
 * Beat at which an in-flight hold auto-releases as a lenient `good`
 * once the player has hung on past the late-release grace. Mirrors the
 * cutoff {@link resolveHoldRelease} uses for the demote-to-good window
 * so the auto-resolve and a manual late release never disagree.
 */
function staleHoldReleaseBeat(config: ManiaEngineConfig, hold: ActiveHold): number {
  return hold.holdEndBeat + config.goodWindowBeats * LATE_RELEASE_GRACE_GOOD_WINDOWS;
}

/** Record one auto-miss for an expired head. Combo/HP handled by {@link applyMissDamage}. */
function autoMissEvent(
  state: ManiaEngineState,
  config: ManiaEngineConfig,
  ev: ManiaLaneEvent,
  events: ManiaEngineEvent[],
): void {
  state.resolvedIds.add(ev.id);
  const resolution: ManiaResolution = {
    laneEventId: ev.id,
    sourceId: ev.sourceId,
    lane: ev.lane,
    kind: 'head',
    verdict: 'miss',
    diffMs: config.perfectOverlapMs + 1,
    beat: ev.beat,
    previousCombo: state.combo,
    scoreDelta: 0,
  };
  state.resolutions.push(resolution);
  state.misses += 1;
  events.push({ type: 'note_judged', beat: ev.beat, payload: resolution });
  applyMissDamage(state, config, ev.beat, events);
}

/**
 * Auto-miss every unresolved lane event whose late edge has passed
 * `toBeat`. Walks events in chronological order and aborts as soon as
 * the player drains — an HP-bleed mid-stream shouldn't keep accumulating
 * ghost misses after death. Used at end-of-chart to force-resolve the
 * tail; the per-beat sweep in {@link advanceTo} drives the in-run case.
 */
function expireMissedEvents(
  state: ManiaEngineState,
  config: ManiaEngineConfig,
  toBeat: number,
  events: ManiaEngineEvent[],
): void {
  for (const ev of config.sortedLaneEvents) {
    if (state.resolvedIds.has(ev.id)) {
      continue;
    }
    if (toBeat <= noteExpiryBeat(config, ev)) {
      continue;
    }
    autoMissEvent(state, config, ev, events);
    if (!state.alive) {
      return;
    }
  }
}

/**
 * Auto-resolve any in-flight hold whose late-release grace has
 * expired. Mirrors {@link expireMissedEvents} but operates on
 * {@link ManiaEngineState.heldLanes} — a player who keeps a key
 * pressed past `holdEndBeat + LATE_RELEASE_GRACE_GOOD_WINDOWS ×
 * goodWindow` gets a `good` release at the grace cutoff (lenient
 * — the body language is "completed", just sloppy).
 */
function expireStaleHolds(
  state: ManiaEngineState,
  config: ManiaEngineConfig,
  toBeat: number,
  events: ManiaEngineEvent[],
): void {
  if (state.heldLanes.size === 0) {
    return;
  }
  // Snapshot the entries so we can mutate `heldLanes` mid-iteration
  // (resolveHoldRelease deletes the entry).
  const stale: ActiveHold[] = [];
  for (const hold of state.heldLanes.values()) {
    if (toBeat > staleHoldReleaseBeat(config, hold)) {
      stale.push(hold);
    }
  }
  // Sort by (holdEndBeat asc, lane asc). Chord-holds tie on the
  // first key (both halves share holdEndBeat) so the lane tiebreak
  // pins the iteration order — `previousCombo` on each resolution
  // and the order of FX events both depend on this being
  // deterministic.
  stale.sort((a, b) => {
    if (a.holdEndBeat !== b.holdEndBeat) {
      return a.holdEndBeat - b.holdEndBeat;
    }
    return a.lane - b.lane;
  });
  for (const hold of stale) {
    resolveHoldRelease(state, config, hold, staleHoldReleaseBeat(config, hold), events);
    if (!state.alive) {
      return;
    }
  }
}

function applyPassiveRegen(
  state: ManiaEngineState,
  fromBeat: number,
  toBeat: number,
  events: ManiaEngineEvent[],
): void {
  if (state.combo <= 0) {
    return;
  }
  const quietStart =
    state.lastMissBeat === null ? -Infinity : state.lastMissBeat + HP_REGEN_QUIET_BEATS;
  const eligibleStart = Math.max(fromBeat, quietStart);
  if (eligibleStart >= toBeat) {
    return;
  }
  state.regenProgressBeats += toBeat - eligibleStart;
  while (state.regenProgressBeats >= HP_REGEN_INTERVAL_BEATS) {
    state.regenProgressBeats -= HP_REGEN_INTERVAL_BEATS;
    if (state.hp >= state.maxHp) {
      continue;
    }
    applyHpDelta(state, toBeat, HP_REGEN_PER_TICK, 'regen', events);
  }
}

/**
 * Earliest auto-resolution trigger strictly before `toBeat`, or `null`
 * if none. Considers unresolved head expiries and in-flight stale-hold
 * releases. Because the expiry/release offsets are chart-wide constants,
 * the first unresolved lane event carries the minimum head expiry, so
 * the scan stops at it rather than walking the whole chart.
 */
function nextAutoResolveBeat(
  state: ManiaEngineState,
  config: ManiaEngineConfig,
  toBeat: number,
): number | null {
  let best: number | null = null;
  for (const ev of config.sortedLaneEvents) {
    if (state.resolvedIds.has(ev.id)) {
      continue;
    }
    const expiry = noteExpiryBeat(config, ev);
    if (expiry < toBeat) {
      best = expiry;
    }
    break;
  }
  for (const hold of state.heldLanes.values()) {
    const releaseBeat = staleHoldReleaseBeat(config, hold);
    if (releaseBeat < toBeat && (best === null || releaseBeat < best)) {
      best = releaseBeat;
    }
  }
  return best;
}

/**
 * Fire every auto-resolution whose trigger lands exactly on `beat`:
 * head auto-misses first (in `sortedLaneEvents` order), then stale-hold
 * auto-releases (holdEndBeat asc, lane asc). Heads before releases at a
 * coincident beat matches the historical phase order so `previousCombo`
 * and FX ordering stay deterministic. Aborts on death.
 */
function resolveAutoEventsAt(
  state: ManiaEngineState,
  config: ManiaEngineConfig,
  beat: number,
  events: ManiaEngineEvent[],
): void {
  for (const ev of config.sortedLaneEvents) {
    if (state.resolvedIds.has(ev.id)) {
      continue;
    }
    // `nextAutoResolveBeat` picks `beat` as the minimum unresolved
    // expiry, so this fires the heads sitting exactly on `beat`. The
    // `<=` (via the `> beat` break) rather than an exact `=== beat`
    // ensures a miss is never silently skipped even if an earlier head
    // were somehow still open at this point.
    if (noteExpiryBeat(config, ev) > beat) {
      break;
    }
    autoMissEvent(state, config, ev, events);
    if (!state.alive) {
      return;
    }
  }
  if (state.heldLanes.size === 0) {
    return;
  }
  const stale: ActiveHold[] = [];
  for (const hold of state.heldLanes.values()) {
    if (staleHoldReleaseBeat(config, hold) === beat) {
      stale.push(hold);
    }
  }
  stale.sort((a, b) => {
    if (a.holdEndBeat !== b.holdEndBeat) {
      return a.holdEndBeat - b.holdEndBeat;
    }
    return a.lane - b.lane;
  });
  for (const hold of stale) {
    resolveHoldRelease(state, config, hold, beat, events);
    if (!state.alive) {
      return;
    }
  }
}

/**
 * Advance the simulation clock to `toBeat`, applying everything that
 * happens passively in between. Walks `(state.beat, toBeat]` as a
 * chronological sweep: passive HP regen accrues continuously across
 * each gap, then the next auto-resolution (head auto-miss or stale-hold
 * release) fires at its exact trigger beat, then the next gap, and so
 * on.
 *
 * Beat-ordered processing is what makes the engine step-invariant —
 * splitting one big advance into many small ones (or vice versa) yields
 * an identical result. That invariance is load-bearing: the live client
 * advances in variable-size audio frames while the replay verifier
 * re-simulates in fixed ticks, and the two outcomes must agree or the
 * run is rejected as a mismatch. The old "expire every miss, then every
 * stale hold, then regen the whole interval" phase order broke this —
 * a miss zeroes combo, so regen over the whole interval was suppressed
 * even for the quiet beats before the miss, and a coarse frame lost far
 * more regen than the fine re-sim.
 */
function advanceTo(
  state: ManiaEngineState,
  config: ManiaEngineConfig,
  toBeat: number,
  events: ManiaEngineEvent[],
): void {
  if (toBeat <= state.beat) {
    return;
  }
  while (state.alive) {
    const next = nextAutoResolveBeat(state, config, toBeat);
    if (next === null) {
      break;
    }
    applyPassiveRegen(state, state.beat, next, events);
    state.beat = next;
    resolveAutoEventsAt(state, config, next, events);
  }
  if (state.alive) {
    applyPassiveRegen(state, state.beat, toBeat, events);
    state.beat = toBeat;
  }
}

function handleTap(
  state: ManiaEngineState,
  config: ManiaEngineConfig,
  inp: ManiaEngineInput,
  events: ManiaEngineEvent[],
): void {
  // Held-lane no-op: the player is already pressing this lane for an
  // in-flight hold, so a stray `down` event (e.g. from a second
  // pointer or keyboard key bound to the same lane) shouldn't count
  // as an empty tap. It's the same finger that engaged the hold.
  if (state.heldLanes.has(inp.lane)) {
    events.push({ type: 'tap', beat: inp.beat, payload: { lane: inp.lane, hit: false } });
    return;
  }
  const target = findClosestOpenEvent(
    config.sortedLaneEvents,
    state.resolvedIds,
    inp,
    config,
  );
  if (target) {
    resolveTap(state, config, target, inp, events);
    events.push({ type: 'tap', beat: inp.beat, payload: { lane: inp.lane, hit: true } });
    return;
  }
  events.push({ type: 'tap', beat: inp.beat, payload: { lane: inp.lane, hit: false } });
  // A tap that lands on the loop's opening beat (0) is a lead-in / count-in
  // strum — the client clamps pre-roll presses (negative audio-clock beats)
  // up to beat 0, and the player is strumming along to the countdown with no
  // fair read on any lane yet. Never let an empty one chip HP or break combo:
  // on looped play the carried hit counters have long since cleared the
  // onboarding grace, so without this guard a stray warm-up strum on loop 2+
  // would nuke the run's combo. A strum that actually lands on a beat-0 note
  // still resolves above via the `target` branch — only genuinely empty
  // count-in strums reach here.
  const isLeadInStrum = inp.beat <= 0;
  const objectNearby = hasUnresolvedEventNearby(
    config.sortedLaneEvents,
    state.resolvedIds,
    inp.lane,
    inp.beat,
  );
  const successfulHits = state.perfects + state.goods;
  if (!isLeadInStrum && objectNearby && successfulHits >= HP_EMPTY_TAP_GRACE_HITS) {
    applyHpDelta(state, inp.beat, HP_EMPTY_TAP, 'empty_tap', events);
  }
}

function handleRelease(
  state: ManiaEngineState,
  config: ManiaEngineConfig,
  inp: ManiaEngineInput,
  events: ManiaEngineEvent[],
): void {
  // `up` on a lane with no active hold is silently ignored — the
  // engine doesn't need to acknowledge stray releases (the renderer
  // tracks pressed-lane glow independently from input source state).
  const hold = state.heldLanes.get(inp.lane);
  if (!hold) {
    return;
  }
  resolveHoldRelease(state, config, hold, inp.beat, events);
}

export function step(input: ManiaStepInput): ManiaStepOutput {
  const { config, fromBeat } = input;
  const state = input.state;
  const events: ManiaEngineEvent[] = [];

  // Never advance the simulation past the loop length. The live client
  // derives `toBeat` from the audio clock, which can overshoot
  // `totalBeats` on a lag-spike frame, whereas the replay verifier caps
  // every tick at `totalBeats`. The end-of-chart auto-miss sweep keys
  // its expiry window off the current beat, so an overshot `toBeat`
  // force-misses notes sitting within ~a beat of the end that a capped
  // run leaves unjudged — diverging the live outcome from the re-sim and
  // tripping `replay_mismatch`. Clamping here is the single chokepoint
  // both client and server pass through, so end-of-chart judgement stays
  // step-invariant for every mode.
  const toBeat = Math.min(input.toBeat, config.chart.totalBeats);

  if (!state.alive || state.completed) {
    return { state, events };
  }

  // Process inputs in chronological order. Both `down` (tap / start
  // hold) and `up` (end hold) are scoring events; stable ordering at
  // coincident beats puts `down` first so a same-beat down+up pair
  // (instant tap-and-release on a zero-length hold, if such ever
  // existed) resolves head before release.
  const sorted = [...input.inputs]
    .filter((inp) => inp.beat >= fromBeat && inp.beat <= toBeat)
    .sort((a, b) => {
      if (a.beat !== b.beat) {
        return a.beat - b.beat;
      }
      if (a.type !== b.type) {
        return a.type === 'down' ? -1 : 1;
      }
      return a.lane - b.lane;
    });

  for (const inp of sorted) {
    advanceTo(state, config, inp.beat, events);
    if (!state.alive || state.completed) {
      break;
    }
    if (inp.type === 'down') {
      handleTap(state, config, inp, events);
    } else {
      handleRelease(state, config, inp, events);
    }
    if (!state.alive) {
      break;
    }
  }

  if (state.alive && !state.completed) {
    advanceTo(state, config, toBeat, events);
  }

  if (state.alive && !state.completed && state.beat >= config.chart.totalBeats) {
    expireMissedEvents(
      state,
      config,
      state.beat + config.perfectOverlapBeats + 1e-6,
      events,
    );
    if (state.alive) {
      // Force-resolve any holds still in flight at end-of-chart. The
      // grace window has effectively run out — better to credit the
      // player with a `good` than to leave dangling holds blocking
      // completion (or worse, silently dropping the release counter).
      expireStaleHolds(
        state,
        config,
        state.beat + config.goodWindowBeats * (LATE_RELEASE_GRACE_GOOD_WINDOWS + 1),
        events,
      );
    }
    if (state.alive) {
      state.completed = true;
      events.push({ type: 'completed', beat: state.beat });
    }
  }

  return { state, events };
}

export function summariseManiaAccuracy(state: ManiaEngineState): number {
  // Mania has no OK tier — pass 0 for the OK count slot of computeAccuracy.
  return computeAccuracy(state.perfects, state.goods, 0, state.misses);
}

export { buildLaneEventLookup };
